/**
 * Bounded Supabase reads.
 *
 * PostgREST (behind Kong) refuses a URL past ~231 UUIDs in one `.in()` filter — supabase-js then
 * returns `{ data: null, error: { message: 'URI too long' } }`, a plain object, not an Error — and
 * silently caps every read at `max_rows` (1,000). Code that ignores the error renders blanks;
 * code that trusts one read loses rows. These helpers make such reads complete and loud: chunk
 * the ids, page the rows, throw on any failure.
 *
 * Isomorphic on purpose (no server-only imports): `src/models/eventModel.ts` uses them and is
 * also bundled for the browser.
 */

const DEFAULT_CHUNK_SIZE = 100;
const DEFAULT_PAGE_SIZE = 1000;
/** Batches in flight at once; keeps a large id list from opening dozens of connections. */
const MAX_PARALLEL_CHUNKS = 4;
const MAX_PAGES = 1000;

type QueryResult<T> = { data: T[] | null; error: unknown };

export class SupabaseQueryError extends Error {
  constructor(
    message: string,
    readonly detail: unknown,
  ) {
    super(message);
    this.name = 'SupabaseQueryError';
  }
}

function describeError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (error && typeof error === 'object' && 'message' in error) {
    const message = (error as { message: unknown }).message;
    if (typeof message === 'string') return message.trim();
  }
  return String(error);
}

function assertPositiveInteger(name: string, value: number): void {
  if (!Number.isInteger(value) || value < 1) {
    throw new RangeError(`${name} must be a positive integer, got ${value}`);
  }
}

/**
 * Runs `run` once per batch of ≤`size` distinct ids (at most 4 at a time; duplicates are dropped
 * first so results never depend on where batch boundaries fall), concatenates the batches in input
 * order — rows within a batch keep the query's own order — and throws `SupabaseQueryError` on any batch's `{ error }` — including supabase-js's
 * non-Error 414 shape. An exception thrown by `run` propagates unchanged. Empty ids → [] with no
 * query.
 */
export async function inChunks<T>(
  ids: readonly string[],
  run: (chunk: string[]) => PromiseLike<QueryResult<T>>,
  size: number = DEFAULT_CHUNK_SIZE,
): Promise<T[]> {
  assertPositiveInteger('inChunks size', size);
  if (ids.length === 0) return [];

  const unique = [...new Set(ids)];
  const chunks: string[][] = [];
  for (let i = 0; i < unique.length; i += size) chunks.push(unique.slice(i, i + size));

  const results: T[][] = new Array(chunks.length);
  let next = 0;
  let failed = false;
  const worker = async (): Promise<void> => {
    while (!failed && next < chunks.length) {
      const index = next++;
      try {
        const { data, error } = await run(chunks[index]);
        if (error) {
          throw new SupabaseQueryError(
            `Batch ${index + 1} of ${chunks.length} failed: ${describeError(error)}`,
            error,
          );
        }
        results[index] = data ?? [];
      } catch (e) {
        failed = true;
        throw e;
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(MAX_PARALLEL_CHUNKS, chunks.length) }, () => worker()),
  );

  const rows: T[] = [];
  for (const batch of results) rows.push(...batch);
  return rows;
}

/**
 * Pages a query with PostgREST's inclusive `.range(from, to)`, advancing by the rows each page
 * actually returned and stopping only on an empty page — so a server `max_rows` below `pageSize`
 * cannot end the read early (it costs one extra empty read). Throws `SupabaseQueryError` on any
 * page's `{ error }`.
 *
 * The query must have a deterministic order (end on a unique column such as `id`), or pages can
 * repeat or skip rows.
 *
 * `stopOnShortPage` ends the read on the first page shorter than `pageSize`, saving that empty
 * read. It is only complete while the server's `max_rows` is at least `pageSize`, so reserve it
 * for hot paths whose result is normally tiny (e.g. a poll for recent changes).
 */
export async function selectAllRows<T>(
  page: (from: number, to: number) => PromiseLike<QueryResult<T>>,
  pageSize: number = DEFAULT_PAGE_SIZE,
  options: { stopOnShortPage?: boolean } = {},
): Promise<T[]> {
  assertPositiveInteger('selectAllRows pageSize', pageSize);
  const rows: T[] = [];
  for (let from = 0, pages = 0; ; ) {
    if (++pages > MAX_PAGES) {
      // A range-ignoring caller (or test mock) would otherwise loop forever.
      throw new SupabaseQueryError(`selectAllRows read more than ${MAX_PAGES} pages`, null);
    }
    const { data, error } = await page(from, from + pageSize - 1);
    if (error) {
      throw new SupabaseQueryError(
        `Page starting at row ${from} failed: ${describeError(error)}`,
        error,
      );
    }
    const batch = data ?? [];
    if (batch.length === 0) return rows;
    rows.push(...batch);
    if (options.stopOnShortPage && batch.length < pageSize) return rows;
    from += batch.length;
  }
}
